// Copyright ©️ 2026 eVolve MEP, LLC

import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls';

import { isNil, isNotNil } from 'helpers/isNotNil';
import type { BendPreviewGeometry } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendPreviewGeometry';
import { disposeGroup } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/helpers';
import { DimensionLabel } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/DimensionLabel';
import {
  type HighlightableObjectName,
  getHighlightableObjectName,
  HighlightableGroup,
} from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/HighlightableGroup';
import { labelTheming } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/labelTheming';
import { getLabelBoundsNDC } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/leaderLineLayout';

export type RenderMode = '3d' | '2dTop' | '2dFront';

/**
 * Space in CSS pixels at each edge of the canvas that is covered by other UI (e.g. floating panels).
 * The camera fits and centers the bend in the remaining area, and labels are kept inside it.
 */
export type FitInsets = {
  /** CSS pixels */
  top: number;
  /** CSS pixels */
  right: number;
  /** CSS pixels */
  bottom: number;
  /** CSS pixels */
  left: number;
};

const NO_INSETS: FitInsets = { top: 0, right: 0, bottom: 0, left: 0 };

const ORTHOGRAPHIC_FIT_PADDING = 1.5;
const PERSPECTIVE_FIT_PADDING = 1.3;
const PERSPECTIVE_MIN_DISTANCE = 2;
const PERSPECTIVE_MAX_DISTANCE = 50;
const PERSPECTIVE_CAMERA_ORIGIN = new THREE.Vector3(-10, 5, -5);
/** In OrbitControls' units, where 2 is one turn every 30 seconds at 60 fps. */
const AUTO_ORBIT_SPEED = 0.35;

export class BendPreviewRenderer {
  public readonly scene: THREE.Scene;
  public readonly cameras: {
    perspective: THREE.PerspectiveCamera;
    orthographic: THREE.OrthographicCamera;
  };
  renderer: THREE.WebGLRenderer;
  controls: OrbitControls;
  previewGroup: THREE.Group;
  private currentGeometry: BendPreviewGeometry | null = null;
  private cachedBoundingBox = new THREE.Box3();
  private currentMode: RenderMode = '3d';
  private activeCamera: THREE.PerspectiveCamera | THREE.OrthographicCamera;
  // The raycaster is used to detect mouse interactions with elements such as labels.
  private readonly raycaster = new THREE.Raycaster();
  private highlightedObject: HighlightableObjectName | null = null;
  private fitInsets: FitInsets = NO_INSETS;
  private labelBoundsNDC = getLabelBoundsNDC();
  private autoOrbitStarted = false;
  private autoOrbitStopped = false;

  constructor(
    public readonly canvas: HTMLCanvasElement,
    public onHighlightedObjectChange: (objectName: HighlightableObjectName | null) => void,
  ) {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xffffff);

    const { aspect } = this;
    const orthoHalfWidth = 10 * aspect;

    this.cameras = {
      perspective: new THREE.PerspectiveCamera(75, aspect, 0.1, 1000),
      orthographic: new THREE.OrthographicCamera(-orthoHalfWidth, orthoHalfWidth, 10, -10, 0.1, 1000),
    };
    this.scene.add(this.cameras.perspective);
    this.scene.add(this.cameras.orthographic);

    this.cameras.perspective.position.copy(PERSPECTIVE_CAMERA_ORIGIN);
    this.cameras.perspective.lookAt(new THREE.Vector3(0, 0, 0));

    /** The perspective camera can see all layers. The orthographic camera's layers are set per mode in animate(). */
    this.cameras.perspective.layers.enable(labelTheming.layers.front);
    this.cameras.perspective.layers.enable(labelTheming.layers.top);

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    this.renderer.setClearColor(0xffffff, 1);
    this.renderer.setSize(canvas.width, canvas.height);
    this.renderer.setPixelRatio(window.devicePixelRatio);

    this.controls = new OrbitControls(this.cameras.perspective, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.minDistance = PERSPECTIVE_MIN_DISTANCE;
    this.controls.maxDistance = PERSPECTIVE_MAX_DISTANCE;
    this.controls.maxPolarAngle = Math.PI / 2;
    this.controls.autoRotateSpeed = AUTO_ORBIT_SPEED;

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
    this.scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(0, 10, 5);
    this.scene.add(directionalLight);

    const grid = new THREE.GridHelper(100, 100, labelTheming.colors.background, labelTheming.colors.backgroundLight);
    this.scene.add(grid);

    this.previewGroup = new THREE.Group();
    this.scene.add(this.previewGroup);

    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.setLabelsVisible = this.setLabelsVisible.bind(this);
    this.stopAutoOrbit = this.stopAutoOrbit.bind(this);
    this.dispose = this.dispose.bind(this);

    this.activeCamera = this.cameras.perspective;
    this.canvas.addEventListener('mousemove', this.onMouseMove);
    this.canvas.addEventListener('mouseleave', this.onMouseLeave);
    // Any drag, zoom or pan by the user ends the orbit.
    this.controls.addEventListener('start', this.stopAutoOrbit);
  }

  dispose() {
    this.canvas.removeEventListener('mousemove', this.onMouseMove);
    this.canvas.removeEventListener('mouseleave', this.onMouseLeave);
    this.controls.removeEventListener('start', this.stopAutoOrbit);
    this.renderer.setAnimationLoop(null);
    this.renderer.dispose();
    this.controls.dispose();
    disposeGroup(this.scene);
    this.scene.clear();
  }

  private get aspect() {
    return this.canvas.width / this.canvas.height;
  }

  /** The canvas size in CSS pixels. */
  private get cssSize() {
    return this.renderer.getSize(new THREE.Vector2());
  }

  /**
   * The size of the area not covered by {@link fitInsets}, as a fraction of the full canvas size.
   * Falls back to the full canvas on an axis where the insets leave no room.
   */
  private get freeAreaFraction() {
    const { x: width, y: height } = this.cssSize;
    const { top, right, bottom, left } = this.fitInsets;
    const freeWidth = width - left - right;
    const freeHeight = height - top - bottom;
    return new THREE.Vector2(
      width > 0 && freeWidth > 0 ? freeWidth / width : 1,
      height > 0 && freeHeight > 0 ? freeHeight / height : 1,
    );
  }

  /**
   * Sets the space covered by other UI at each edge of the canvas. The camera re-fits so the bend is
   * centered in the remaining area, and labels are kept inside it.
   */
  setFitInsets(insets: FitInsets) {
    this.fitInsets = insets;
    this.applyFitInsets();
    this.fitActiveCamera(this.cachedBoundingBox);
  }

  /**
   * Shifts both cameras' projection centers to the center of the free area, and recomputes the label bounds.
   * Must be re-applied whenever the canvas size or the insets change.
   */
  private applyFitInsets() {
    const { x: width, y: height } = this.cssSize;
    const { top, right, bottom, left } = this.fitInsets;
    const { x: freeWidthFraction, y: freeHeightFraction } = this.freeAreaFraction;
    // Only shift an axis the insets leave room on, matching freeAreaFraction's fallback.
    const offsetX = freeWidthFraction < 1 ? (left - right) / 2 : 0;
    const offsetY = freeHeightFraction < 1 ? (top - bottom) / 2 : 0;

    [this.cameras.perspective, this.cameras.orthographic].forEach((camera) => {
      if (width === 0 || height === 0 || (offsetX === 0 && offsetY === 0)) {
        camera.clearViewOffset();
      } else {
        // Viewing a same-sized window at a negative offset moves the full view's center by (offsetX, offsetY).
        camera.setViewOffset(width, height, -offsetX, -offsetY, width, height);
      }
    });

    // Convert the free area from CSS pixels to NDC. NDC Y points up, CSS Y points down.
    const freeCenterNDC = new THREE.Vector2(
      width > 0 ? (2 * offsetX) / width : 0,
      height > 0 ? (-2 * offsetY) / height : 0,
    );
    const freeHalfExtentsNDC = this.freeAreaFraction;
    this.labelBoundsNDC = getLabelBoundsNDC(
      new THREE.Box2(freeCenterNDC.clone().sub(freeHalfExtentsNDC), freeCenterNDC.clone().add(freeHalfExtentsNDC)),
    );
  }

  /**
   * Slowly orbits the 3D camera around the bend, to show that the view can be turned. Does nothing once the orbit has
   * been stopped, by {@link stopAutoOrbit} or by the user moving the view.
   */
  startAutoOrbit() {
    if (this.autoOrbitStopped) return;
    this.controls.autoRotate = true;
    this.autoOrbitStarted = true;
  }

  /** Stops the orbit started by {@link startAutoOrbit} for good, leaving the camera where it is. */
  stopAutoOrbit() {
    this.autoOrbitStopped = true;
    this.controls.autoRotate = false;
  }

  /**
   * Stops the orbit for good and, if it ever ran, turns the 3D camera back to its starting angle, so the next bend
   * isn't shown from wherever the orbit or the user left the camera.
   */
  endAutoOrbit() {
    this.stopAutoOrbit();
    if (!this.autoOrbitStarted) return;
    this.autoOrbitStarted = false;
    this.controls.reset();
    this.fitActiveCamera(this.cachedBoundingBox);
  }

  setLabelsVisible(visible: boolean) {
    if (this.currentGeometry) {
      this.currentGeometry.labelGroup.visible = visible;
    }
  }

  /**
   * Replaces the current geometry with a new one and re-fits the camera.
   * Call after creating a {@link BendPreviewGeometry} and invoking `createBendPreview` on it.
   * The previous geometry is removed from the scene but NOT disposed — the caller is responsible
   * for disposing geometries it created.
   */
  setGeometry(geometry: BendPreviewGeometry) {
    this.clearGeometry();
    this.currentGeometry = geometry;
    this.previewGroup.add(geometry.rootGroup);
    this.onGeometryChange();
  }

  /**
   * Removes the current geometry from the scene without disposing it.
   * The caller remains responsible for calling `geometry.dispose()`.
   */
  clearGeometry() {
    if (this.currentGeometry) {
      this.previewGroup.remove(this.currentGeometry.rootGroup);
      this.currentGeometry = null;
    }
  }

  /**
   * Updates the size of the renderer and camera. Call this whenever the canvas size changes.
   * @param width the new width of the canvas
   * @param height the new height of the canvas
   */
  updateSize(width: number, height: number) {
    if (width === 0 || height === 0) return;
    this.renderer.setSize(width, height);
    this.cameras.perspective.aspect = this.aspect;
    this.cameras.perspective.updateProjectionMatrix();
    this.applyFitInsets();
    this.fitActiveCamera(this.cachedBoundingBox);
  }

  /**
   * Centers the geometry and fits the camera. Called by {@link setGeometry}.
   */
  private onGeometryChange() {
    if (!this.currentGeometry) return;
    const boundingBox = this.currentGeometry.computeAndCenter();
    if (!boundingBox) return;
    this.cachedBoundingBox.copy(boundingBox);
    this.fitActiveCamera(this.cachedBoundingBox);
  }

  private fitOrthographicCamera(box: THREE.Box3) {
    if (box.isEmpty()) return;
    const { aspect } = this;
    const size = box.getSize(new THREE.Vector3());
    // Top camera looks down Y: horizontal = X, vertical = Z
    // Front camera looks along X: horizontal = Z, vertical = Y
    const horizontalHalfExtent = this.currentMode === '2dFront' ? size.z / 2 : size.x / 2;
    const verticalHalfExtent = this.currentMode === '2dTop' ? size.z / 2 : size.y / 2;

    // verticalHalfExtent * padding: fit the geometry's vertical extent with breathing room
    // (xHalfExtent * padding) / aspect: fit the geometry's horizontal extent with breathing room, scaled down to height units
    // Dividing by the free area fraction fits the geometry into the area not covered by the fit insets.
    const freeAreaFraction = this.freeAreaFraction;
    const halfHeight = Math.max(
      (verticalHalfExtent * ORTHOGRAPHIC_FIT_PADDING) / freeAreaFraction.y,
      (horizontalHalfExtent * ORTHOGRAPHIC_FIT_PADDING) / aspect / freeAreaFraction.x,
    );
    const camera = this.cameras.orthographic;
    camera.left = -halfHeight * aspect;
    camera.right = halfHeight * aspect;
    camera.top = halfHeight;
    camera.bottom = -halfHeight;

    // In 2dFront mode, centering places geometry so its bottom is at Y=0 (all positive Y), so
    // we move the camera to the vertical center of the box so the frustum is centered on it.
    if (this.currentMode === '2dFront') {
      const centerY = box.getCenter(new THREE.Vector3()).y;
      camera.position.setY(centerY);
    }

    camera.updateProjectionMatrix();
  }

  private fitPerspectiveCamera(box: THREE.Box3) {
    if (box.isEmpty()) return;

    const camera = this.cameras.perspective;

    // getViewSize(1) returns the full width/height of the frustum at distance 1.
    // Halving gives tan(fov/2), which is the correct divisor for fitting a sphere.
    // Scaling by the free area fraction fits the sphere into the area not covered by the fit insets.
    const halfUnitView = camera.getViewSize(1, new THREE.Vector2()).multiplyScalar(0.5).multiply(this.freeAreaFraction);
    const { radius, center: boxCenter } = box.getBoundingSphere(new THREE.Sphere());
    const distance = Math.max(radius / halfUnitView.x, radius / halfUnitView.y) * PERSPECTIVE_FIT_PADDING;

    // Preserve the current viewing angle; fall back to the default origin if the
    // camera is already at the box center (which would produce a zero-length vector).
    const toCamera = camera.position.clone().sub(boxCenter);
    const direction =
      toCamera.lengthSq() > 1e-10 ? toCamera.normalize() : PERSPECTIVE_CAMERA_ORIGIN.clone().normalize();

    camera.position.copy(boxCenter).addScaledVector(direction, distance);
    this.controls.target.copy(boxCenter);
    this.controls.update();
    camera.updateProjectionMatrix();
  }

  private fitActiveCamera(box: THREE.Box3) {
    if (this.currentMode === '3d') {
      this.fitPerspectiveCamera(box);
    } else {
      this.fitOrthographicCamera(box);
    }
  }

  private placeLabels() {
    const labelGroup = this.currentGeometry?.labelGroup;
    if (isNil(labelGroup) || !labelGroup.visible) return;

    const labels = labelGroup.children.filter((c): c is DimensionLabel => c instanceof DimensionLabel);

    DimensionLabel.updateAll(labels, this.activeCamera, this.cachedBoundingBox, this.labelBoundsNDC);
  }

  /**
   * Starts the animation loop. Call this after the preview group is set up.
   * @returns a function to stop the animation loop.
   */
  animate(mode: RenderMode) {
    this.currentMode = mode;

    if (mode === '2dTop') {
      // up=(0,0,1) orients the top view so the bend runs vertically on screen (vs. horizontally in the
      // front view), making the two views immediately distinguishable. It also ensures that directional
      // bends like "kick 90 right" look correct — the stub visually goes left or right as expected.
      this.cameras.orthographic.position.set(0, 10, 0);
      this.cameras.orthographic.up.set(0, 0, 1);
      this.cameras.orthographic.lookAt(0, 0, 0);
      this.cameras.orthographic.layers.disable(labelTheming.layers.front);
      this.cameras.orthographic.layers.enable(labelTheming.layers.top);
      this.raycaster.layers.enable(labelTheming.layers.top);
      this.raycaster.layers.disable(labelTheming.layers.front);
    } else if (mode === '2dFront') {
      // Look at the YZ plane
      this.cameras.orthographic.position.set(-10, 0, 0);
      this.cameras.orthographic.up.set(0, 1, 0);
      this.cameras.orthographic.lookAt(0, 0, 0);
      this.cameras.orthographic.layers.disable(labelTheming.layers.top);
      this.cameras.orthographic.layers.enable(labelTheming.layers.front);
      this.raycaster.layers.enable(labelTheming.layers.front);
      this.raycaster.layers.disable(labelTheming.layers.top);
    } else {
      this.raycaster.layers.enable(labelTheming.layers.front);
      this.raycaster.layers.enable(labelTheming.layers.top);
    }

    const camera = mode === '3d' ? this.cameras.perspective : this.cameras.orthographic;
    this.activeCamera = camera;
    this.controls.enabled = mode === '3d';

    this.fitActiveCamera(this.cachedBoundingBox);

    const getFrame = () => {
      if (mode === '3d') {
        this.controls.update();
      }
      this.placeLabels();
      this.renderer.render(this.scene, camera);
    };
    this.renderer.setAnimationLoop(getFrame);
    return () => this.renderer.setAnimationLoop(null);
  }

  public updateHighlightedObject(objectName: HighlightableObjectName | null) {
    const currentHighlighted = this.highlightedObject;
    if (objectName === currentHighlighted) return;
    const labelGroup = this.currentGeometry?.labelGroup;
    labelGroup?.children.forEach((child) => {
      if (child instanceof HighlightableGroup) {
        if (child.highlightableObjectName === objectName) {
          child.highlight();
        } else {
          child.unhighlight();
        }
      }
    });

    this.highlightedObject = objectName;
  }

  private onMouseMove(event: MouseEvent) {
    const rect = this.canvas.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(new THREE.Vector2(x, y), this.activeCamera);
    const labelGroup = this.currentGeometry?.labelGroup;
    const intersections = labelGroup ? this.raycaster.intersectObjects(labelGroup.children, true) : [];

    let labelHit: HighlightableObjectName | null = null;
    for (const { object } of intersections) {
      const highlightableName = getHighlightableObjectName(object);
      if (isNotNil(highlightableName)) {
        labelHit = highlightableName;
        break;
      }
    }

    this.onHighlightedObjectChange(labelHit);
  }

  private onMouseLeave() {
    this.onHighlightedObjectChange(null);
  }
}
